import { isNil } from 'lodash-es'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { CreationType } from '@/types'
import { getExamAIUsages } from '@/api'

import type { PayloadAction } from '@reduxjs/toolkit'
import type { AIUsage } from '@luigi/examkit/typings'

interface AICreationState {
  tempPrompt?: string
  prompt?: string
  jobId?: string
  channelToken?: string
  loadWithPrompt?: boolean
  withAgentMode?: boolean
  withCache?: boolean
  creationType?: CreationType
  usage?: AIUsage
  creationFailReason?: string
  documentFile?: {
    name: string
    path: string
  }
}

export const aiCreation = createSlice({
  name: 'creating',
  initialState: {} as AICreationState,
  reducers: {
    updateCreationState: (
      state,
      action: PayloadAction<
        Pick<AICreationState, 'creationType' | 'channelToken' | 'jobId' | 'prompt' | 'withAgentMode' | 'withCache'>
      >
    ) => {
      const { creationType, prompt, channelToken, jobId, withAgentMode, withCache } = action.payload
      !isNil(creationType) && (state.creationType = creationType)
      !isNil(prompt) && (state.prompt = prompt)
      !isNil(channelToken) && (state.channelToken = channelToken)
      !isNil(jobId) && (state.jobId = jobId)
      !isNil(withAgentMode) && (state.withAgentMode = withAgentMode)
      !isNil(withCache) && (state.withCache = withCache)
    },
    clearChannelAndJob: state => {
      state.channelToken = undefined
      state.jobId = undefined
    },
    updateTempPrompt: (state, action: PayloadAction<string | null>) => {
      state.tempPrompt = action.payload ?? ''
    },
    setLoadWithPrompt: (state, action: PayloadAction<boolean>) => {
      state.loadWithPrompt = action.payload
    },
    setDocumentFile: (state, action: PayloadAction<Exclude<AICreationState['documentFile'], undefined>>) => {
      state.documentFile = action.payload
    },
    clearDocumentFile: state => {
      if (!state.documentFile) return
      state.documentFile = undefined
    },
    resetCreation: state => {
      state.prompt = undefined
      state.creationType = undefined
      state.withAgentMode = undefined
      state.withCache = undefined
      state.creationFailReason = undefined
      state.jobId = undefined
      state.channelToken = undefined
    },
    setCreationFailReason: (
      state,
      action: PayloadAction<Exclude<AICreationState['creationFailReason'], undefined>>
    ) => {
      state.creationFailReason = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(getAIUsagesThunk.fulfilled, (state, action) => {
      state.usage = action.payload
    })
  },
})

export const getAIUsagesThunk = createAsyncThunk('aiCreation/getUsagesThunk', async () => {
  const { data } = await getExamAIUsages()
  return data
})

export const {
  updateCreationState,
  clearChannelAndJob,
  updateTempPrompt,
  setLoadWithPrompt,
  setDocumentFile,
  clearDocumentFile,
  resetCreation,
  setCreationFailReason,
} = aiCreation.actions

export default aiCreation.reducer
